import { loan_application } from '@axo/shared/data-access/types';
import { useCallback, useMemo, useState } from 'react';
import { useLoanApplicationDependencies } from './useLoanApplicationDependencies';
import { useRenewLoanApplicationDependency } from './useRenewLoanApplicationDependency';

export function useRenewAllFailedDependencies(
  applicationID: string | undefined,
) {
  const { data: dependencies } = useLoanApplicationDependencies(applicationID);
  const { mutateAsync: renewDependency } = useRenewLoanApplicationDependency();

  const [isLoading, setIsLoading] = useState(false);

  const failedDependencies = useMemo(() => {
    if (!dependencies) return [];

    const latestByName = new Map<
      loan_application.DependencyName,
      loan_application.Dependency
    >();

    for (const dep of dependencies) {
      const existing = latestByName.get(dep.dependency_name);
      if (!existing || dep.created_at > existing.created_at) {
        latestByName.set(dep.dependency_name, dep);
      }
    }

    return Array.from(latestByName.values()).filter(
      (dep) => dep.status === loan_application.DependencyStatus.Failed,
    );
  }, [dependencies]);

  const renewAll = useCallback(() => {
    if (!applicationID || isLoading) return;

    setIsLoading(true);

    return Promise.all(
      failedDependencies.map((dep) =>
        renewDependency({ applicationID, dependencyID: dep.id }),
      ),
    ).finally(() => {
      setIsLoading(false);
    });
  }, [applicationID, failedDependencies, renewDependency, isLoading]);

  return {
    renewAll,
    isLoading,
  };
}
